/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {
  Card,
  CardContent,
  CardHeader,
  Stack,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
} from '@mui/material';
import {useFlags} from 'launchdarkly-react-client-sdk';
import React from 'react';
import ContactAudioPlayer from '../../../components/AudioPlayer';
import {FeatureFlagSet, FeatureFlags} from '../../../flags';
import {Voice} from '../hooks/useGetVoices';

type AgentAboutProps = {
  formValues: {
    name: string;
    persona: string;
    greeting: string;
    voiceId: string | undefined;
  };
  handleVoiceChange: (voiceId: string | undefined) => void;
  handleAgentChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isNameInvalid?: boolean;
  isPersonaInvalid?: boolean;
  isGreetingInvalid?: boolean;
  voices?: Voice[];
  isLoadingVoices?: boolean;
};

export const AgentAbout: React.FC<AgentAboutProps> = ({
  formValues,
  handleVoiceChange,
  handleAgentChange,
  isNameInvalid = false,
  isPersonaInvalid = false,
  isGreetingInvalid = false,
  voices,
  isLoadingVoices,
}) => {
  const selectedVoice = voices?.find((voice) => voice.voiceId === formValues.voiceId);
  const selectedVoicePreviewUrl = selectedVoice?.previewUrl || 'https://cdn.openai.com/API/docs/audio/alloy.wav';
  const flags = useFlags<FeatureFlagSet>();

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <TextField
              required
              label={'Name'}
              name={'name'}
              value={formValues.name}
              onChange={handleAgentChange}
              fullWidth
              variant={'outlined'}
              inputProps={{maxLength: 30}}
              error={isNameInvalid}
              helperText={isNameInvalid ? 'Agent name is required.' : ''}
            />

            {flags[FeatureFlags.testAgent] && (
              <>
                <TextField
                  required
                  label={'Greeting'}
                  name={'greeting'}
                  value={formValues.greeting}
                  onChange={handleAgentChange}
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={6}
                  variant={'outlined'}
                  error={isGreetingInvalid}
                  helperText={isGreetingInvalid ? 'Greeting is required.' : ''}
                />
                <FormControl fullWidth>
                  <InputLabel id={'voice-select-label'} shrink>
                    Voice
                  </InputLabel>

                  <Select
                    displayEmpty
                    labelId={'voice-select-label'}
                    id={'voice-select'}
                    name={'voiceId'}
                    value={formValues.voiceId || ''}
                    onChange={(e) => {
                      handleVoiceChange(e.target.value);
                    }}
                    label={'Voice'}
                    disabled={isLoadingVoices}
                  >
                    <MenuItem value={''}>{isLoadingVoices ? 'Loading voices...' : 'Default'}</MenuItem>
                    {voices?.map((voice) => (
                      <MenuItem key={voice.voiceId} value={voice.voiceId}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                          {voice.name}
                          {voice.labels && (
                            <>
                              {['accent', 'gender', 'description', 'age'].map(
                                (field) =>
                                  voice.labels[field] && (
                                    <Chip
                                      key={field}
                                      label={
                                        voice.labels[field].charAt(0).toUpperCase() +
                                        voice.labels[field].slice(1).toLowerCase()
                                      }
                                      size={'small'}
                                      sx={{
                                        backgroundColor: (theme) => {
                                          switch (field) {
                                            case 'gender':
                                              return theme.palette.primary.main;
                                            case 'accent':
                                              return theme.palette.secondary.main;
                                            case 'description':
                                              return theme.palette.success.main;
                                            case 'age':
                                              return theme.palette.warning.main;
                                            default:
                                              return theme.palette.grey[300];
                                          }
                                        },
                                        color: (theme) => {
                                          switch (field) {
                                            case 'gender':
                                              return theme.palette.primary.contrastText;
                                            case 'accent':
                                              return theme.palette.secondary.contrastText;
                                            case 'description':
                                              return theme.palette.success.contrastText;
                                            case 'age':
                                              return theme.palette.warning.contrastText;
                                            default:
                                              return '#fff';
                                          }
                                        },
                                      }}
                                    />
                                  ),
                              )}
                            </>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {selectedVoicePreviewUrl && (
                    <Box
                      sx={{
                        mt: 2,
                        '& audio::-webkit-media-controls-panel': {
                          bgcolor: 'card.main',
                        },
                      }}
                    >
                      <ContactAudioPlayer audioURL={selectedVoicePreviewUrl} />
                    </Box>
                  )}
                </FormControl>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title={'Persona'} />
        <CardContent>
          <Stack spacing={1}>
            <Typography variant={'body2'} color={'textSecondary'}>
              How should the agent interact with the customer?
            </Typography>
            <TextField
              required
              name={'persona'}
              value={formValues.persona}
              onChange={handleAgentChange}
              fullWidth
              multiline
              minRows={4}
              maxRows={26}
              variant={'outlined'}
              placeholder={'You are a friendly customer service agent...'}
              error={isPersonaInvalid}
              helperText={isPersonaInvalid ? 'Persona is required.' : ''}
            />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};
